import { useState, useEffect, useMemo } from 'react';
import { useHolidayStore, Holiday } from '../../../store/holidayStore';
import { useLeaveStore } from '../../../store/leaveStore';
import { useRbacStore } from '../../../store/rbacStore';
import { Button } from '../../../components/Button';
import { Input } from '../../../components/Input';
import { Textarea } from '../../../components/Textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
  DialogDescription, DialogFooter,
} from '../../../components/Dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../../components/Select';
import { Plus, Trash2, Edit2, CalendarDays, ChevronDown } from 'lucide-react';
import toast from 'react-hot-toast';

const MONTH_NAMES = [
  'JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN',
  'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC'
];

const DAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

export function HolidayCalendar() {
  const { holidays, isLoading, fetchHolidays, createHoliday, updateHoliday, deleteHoliday } = useHolidayStore();
  const { submitLeaveRequest, leaveTypes, fetchLeaveTypes } = useLeaveStore();
  const { hasFullAccess, hasPermission } = useRbacStore();

  const canManageHolidays = hasFullAccess() || hasPermission('People', 'Holidays', 'Create');

  const currentCalendarYear = new Date().getFullYear();
  const [selectedYear, setSelectedYear] = useState<number>(currentCalendarYear);

  // Apply dialog state
  const [applyDialogOpen, setApplyDialogOpen] = useState(false);
  const [selectedHolidayForApply, setSelectedHolidayForApply] = useState<Holiday | null>(null);
  const [applyLeaveTypeId, setApplyLeaveTypeId] = useState('');
  const [applyReason, setApplyReason] = useState('');
  const [isSubmittingLeave, setIsSubmittingLeave] = useState(false);

  // Add / Edit holiday dialog state
  const [holidayDialogOpen, setHolidayDialogOpen] = useState(false);
  const [editingHoliday, setEditingHoliday] = useState<Holiday | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    date: '',
    type: 'Public' as 'Public' | 'Optional' | 'Regional',
    description: '',
  });

  useEffect(() => {
    fetchHolidays({ year: selectedYear });
    fetchLeaveTypes();
  }, [selectedYear]);

  // Group holidays by month index (0 = Jan, ..., 11 = Dec)
  const holidaysByMonth = useMemo(() => {
    const map: Record<number, Holiday[]> = {};
    for (let i = 0; i < 12; i++) {
      map[i] = [];
    }

    holidays.forEach((h) => {
      const d = new Date(h.date);
      if (d.getFullYear() === selectedYear) {
        const monthIndex = d.getMonth();
        if (map[monthIndex]) {
          map[monthIndex].push(h);
        }
      }
    });

    // Sort holidays within each month by date
    for (let i = 0; i < 12; i++) {
      map[i].sort((a, b) => new Date(a.date).getDate() - new Date(b.date).getDate());
    }

    return map;
  }, [holidays, selectedYear]);

  // ------------------------------------------------------------- handlers

  const handleOpenApplyDialog = (holiday: Holiday) => {
    setSelectedHolidayForApply(holiday);
    setApplyReason(`Restricted / Optional Holiday: ${holiday.name}`);
    if (leaveTypes.length > 0) {
      // Find matching type or default to first
      const optType = leaveTypes.find(t => t.name.toLowerCase().includes('optional') || t.name.toLowerCase().includes('casual') || t.name.toLowerCase().includes('annual'));
      setApplyLeaveTypeId(optType ? optType.id : leaveTypes[0].id);
    }
    setApplyDialogOpen(true);
  };

  const handleApplyLeave = async () => {
    if (!selectedHolidayForApply) return;
    setIsSubmittingLeave(true);
    try {
      const holidayDateStr = new Date(selectedHolidayForApply.date).toISOString().split('T')[0];
      await submitLeaveRequest({
        leaveTypeId: applyLeaveTypeId,
        startDate: holidayDateStr,
        endDate: holidayDateStr,
        totalDays: 1,
        reason: applyReason || `Optional Holiday: ${selectedHolidayForApply.name}`,
      });
      toast.success(`Leave applied for ${selectedHolidayForApply.name}!`);
      setApplyDialogOpen(false);
      setSelectedHolidayForApply(null);
    } catch (err: any) {
      toast.error(err.response?.data?.message || err.message || 'Failed to submit leave');
    } finally {
      setIsSubmittingLeave(false);
    }
  };

  const handleOpenAddHoliday = () => {
    setEditingHoliday(null);
    setFormData({
      name: '',
      date: `${selectedYear}-01-01`,
      type: 'Public',
      description: '',
    });
    setHolidayDialogOpen(true);
  };

  const handleOpenEditHoliday = (h: Holiday) => {
    setEditingHoliday(h);
    const dateStr = new Date(h.date).toISOString().split('T')[0];
    setFormData({
      name: h.name,
      date: dateStr,
      type: (h.type === 'Optional' || h.type === 'Regional' ? h.type : 'Public') as any,
      description: h.description || '',
    });
    setHolidayDialogOpen(true);
  };

  const handleSaveHoliday = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name || !formData.date) {
      toast.error('Please enter holiday name and date');
      return;
    }

    try {
      if (editingHoliday) {
        await updateHoliday(editingHoliday.id, {
          name: formData.name,
          date: formData.date,
          type: formData.type,
          description: formData.description,
        });
        toast.success('Holiday updated');
      } else {
        await createHoliday({
          name: formData.name,
          date: formData.date,
          type: formData.type,
          description: formData.description,
          isActive: true,
        });
        toast.success('Holiday added');
      }
      setHolidayDialogOpen(false);
      fetchHolidays({ year: selectedYear });
    } catch (e: any) {
      toast.error(e.message || 'Failed to save holiday');
    }
  };

  const handleDeleteHoliday = async (id: string, name: string) => {
    if (!window.confirm(`Are you sure you want to delete "${name}"?`)) return;
    try {
      await deleteHoliday(id);
      toast.success('Holiday deleted');
    } catch (e: any) {
      toast.error('Failed to delete holiday');
    }
  };

  const yearOptions = [
    selectedYear - 2,
    selectedYear - 1,
    selectedYear,
    selectedYear + 1,
    selectedYear + 2,
  ];

  return (
    <div className="space-y-5">
      {/* Top Header matching exact screenshot */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="tracking-tight text-foreground font-display text-page-title">Holiday Calendar</h1>
        </div>

        <div className="flex items-center gap-3 w-full sm:w-auto justify-end">
          {/* Year Dropdown */}
          <div className="relative">
            <select
              value={selectedYear}
              onChange={(e) => setSelectedYear(Number(e.target.value))}
              className="appearance-none bg-card border border-border rounded-md px-3.5 py-1.5 pr-8 text-sm font-semibold text-foreground hover:bg-secondary/40 focus:outline-none focus:ring-2 focus:ring-primary/20 cursor-pointer shadow-2xs"
            >
              {yearOptions.map((yr) => (
                <option key={yr} value={yr}>
                  {yr}
                </option>
              ))}
            </select>
            <ChevronDown className="size-3.5 absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
          </div>

          {/* Admin Add Holiday Button */}
          {canManageHolidays && (
            <Button variant="primary" size="xs" onClick={handleOpenAddHoliday}>
              <Plus className="size-3.5 mr-1" />
              Add Holiday
            </Button>
          )}
        </div>
      </div>

      {/* 12 Months Grid: Exactly 4 columns x 3 rows matching image */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {MONTH_NAMES.map((monthName, monthIndex) => {
          const monthHolidays = holidaysByMonth[monthIndex] || [];
          const hasHolidays = monthHolidays.length > 0;

          return (
            <div
              key={monthName}
              className="bg-card border border-border rounded-lg p-5 shadow-2xs min-h-[175px] flex flex-col transition-all duration-150 hover:border-border-hover"
            >
              {/* Month Header (e.g. JAN 2026) */}
              <div className="text-xs font-semibold text-muted-foreground mb-3.5">
                {monthName} {selectedYear}
              </div>

              {/* Month Content */}
              {!hasHolidays ? (
                <div className="flex-1 flex items-center justify-center py-6 text-xs text-muted-foreground/50 select-none">
                  No Holidays
                </div>
              ) : (
                <div className="space-y-3.5 flex-1">
                  {monthHolidays.map((h) => {
                    const holidayDate = new Date(h.date);
                    const dayNum = String(holidayDate.getDate()).padStart(2, '0');
                    const dayOfWeek = DAY_NAMES[holidayDate.getDay()];
                    const isOptional = h.type.toLowerCase() === 'optional' || h.type.toLowerCase() === 'restricted';

                    return (
                      <div
                        key={h.id}
                        className="group flex items-start justify-between gap-3 text-xs"
                      >
                        {/* Left: Date Block */}
                        <div className="flex flex-col items-center shrink-0 w-8">
                          <span className="text-sm font-semibold text-foreground leading-tight tracking-tight">
                            {dayNum}
                          </span>
                          <span className="text-xs font-medium text-muted-foreground leading-none mt-0.5">
                            {dayOfWeek}
                          </span>
                        </div>

                        {/* Center: Holiday Name */}
                        <div className="flex-1 min-w-0 pt-0.5">
                          <span
                            className="font-medium text-foreground leading-snug break-words block"
                            title={h.description || h.name}
                          >
                            {h.name}
                          </span>
                        </div>

                        {/* Right: Apply action or Admin controls */}
                        <div className="flex items-center gap-2 shrink-0 pt-0.5">
                          {isOptional && (
                            <button
                              type="button"
                              onClick={() => handleOpenApplyDialog(h)}
                              className="text-xs font-semibold text-primary hover:text-primary/80 hover:underline cursor-pointer"
                            >
                              Apply
                            </button>
                          )}

                          {canManageHolidays && (
                            <div className="opacity-0 group-hover:opacity-100 flex items-center gap-1 transition-opacity">
                              <button
                                type="button"
                                onClick={() => handleOpenEditHoliday(h)}
                                className="text-muted-foreground hover:text-foreground p-0.5"
                                title="Edit holiday"
                              >
                                <Edit2 className="size-3" />
                              </button>
                              <button
                                type="button"
                                onClick={() => handleDeleteHoliday(h.id, h.name)}
                                className="text-muted-foreground hover:text-destructive p-0.5"
                                title="Delete holiday"
                              >
                                <Trash2 className="size-3" />
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* ======================= APPLY FOR OPTIONAL HOLIDAY DIALOG ======================= */}
      <Dialog open={applyDialogOpen} onOpenChange={setApplyDialogOpen}>
        <DialogContent className="max-w-[440px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CalendarDays className="size-4 text-primary" /> Apply for Optional Holiday
            </DialogTitle>
            <DialogDescription>
              Submit a leave application for{' '}
              <strong className="text-foreground">{selectedHolidayForApply?.name}</strong> on{' '}
              {selectedHolidayForApply?.date && new Date(selectedHolidayForApply.date).toLocaleDateString(undefined, {
                weekday: 'short',
                month: 'short',
                day: 'numeric',
                year: 'numeric',
              })}.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            <div>
              <label className="block text-xs font-semibold text-muted-foreground mb-1.5">Leave Type *</label>
              <Select value={applyLeaveTypeId} onValueChange={setApplyLeaveTypeId}>
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Select leave type" />
                </SelectTrigger>
                <SelectContent>
                  {leaveTypes.map((lt) => (
                    <SelectItem key={lt.id} value={lt.id}>{lt.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <label className="block text-xs font-semibold text-muted-foreground mb-1.5">Reason</label>
              <Textarea
                rows={2}
                value={applyReason}
                onChange={(e) => setApplyReason(e.target.value)}
                placeholder="Reason for availing optional holiday..."
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="secondary" size="sm" onClick={() => setApplyDialogOpen(false)}>
              Cancel
            </Button>
            <Button variant="primary" size="sm" onClick={handleApplyLeave} isLoading={isSubmittingLeave}>
              Submit Request
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ======================= ADD / EDIT HOLIDAY DIALOG (ADMIN) ======================= */}
      {canManageHolidays && (
        <Dialog open={holidayDialogOpen} onOpenChange={setHolidayDialogOpen}>
          <DialogContent className="max-w-[480px]">
            <DialogHeader>
              <DialogTitle>{editingHoliday ? 'Edit Holiday' : 'Add New Holiday'}</DialogTitle>
              <DialogDescription>
                Configure holiday name, date and whether it is public or optional/restricted.
              </DialogDescription>
            </DialogHeader>

            <form onSubmit={handleSaveHoliday} className="space-y-4 py-2">
              <div>
                <label className="block text-xs font-semibold text-muted-foreground mb-1.5">Holiday Name *</label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="e.g. Tamil New Year / Good Friday"
                  required
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-semibold text-muted-foreground mb-1.5">Date *</label>
                  <Input
                    type="date"
                    value={formData.date}
                    onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                    required
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-muted-foreground mb-1.5">Type *</label>
                  <select
                    value={formData.type}
                    onChange={(e) => setFormData({ ...formData, type: e.target.value as any })}
                    className="w-full px-3 py-2 bg-card border border-border rounded-lg text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                  >
                    <option value="Public">Public (Mandatory)</option>
                    <option value="Optional">Optional / Restricted (Has 'Apply')</option>
                    <option value="Regional">Regional</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-xs font-semibold text-muted-foreground mb-1.5">Description (Optional)</label>
                <Textarea
                  rows={2}
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Additional observance details or statutory note..."
                />
              </div>

              <DialogFooter className="pt-2">
                <Button type="button" variant="secondary" size="sm" onClick={() => setHolidayDialogOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit" variant="primary" size="sm">
                  {editingHoliday ? 'Save Changes' : 'Create Holiday'}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
